import clsx from 'clsx';
import { useCallback, useState } from '@wordpress/element';
import { parseQuantityAndUnitFromRawValue } from '../../unit-control/utils';
import type { WordPressComponentProps } from '../../context';
import { useContextSystem } from '../../context';
import styles from '../style.module.scss';
import type { Border, BorderControlProps } from '../types';

// If either width or color are defined, the border is considered valid
// and a border style can be set as well.
const isValidBorder = ( border?: Border ) => {
	const hasWidth = border?.width !== undefined && border.width !== '';
	const hasColor = border?.color !== undefined;
	return hasWidth || hasColor;
};

export function useBorderControl(
	props: WordPressComponentProps< BorderControlProps, 'div' >
) {
	const {
		className,
		colors = [],
		isCompact,
		onChange,
		enableAlpha = true,
		enableStyle = true,
		shouldSanitizeBorder = true,
		value: border,
		width,
		__experimentalIsRenderedInSidebar = false,
		// Deprecated props, no longer used.
		size: _size,
		__next40pxDefaultSize: _next40pxDefaultSize,
		...otherProps
	} = useContextSystem( props, 'BorderControl' );

	const [ widthValue, originalWidthUnit ] = parseQuantityAndUnitFromRawValue(
		border?.width
	);
	const widthUnit = originalWidthUnit || 'px';
	const hadPreviousZeroWidth = widthValue === 0;

	const [ colorSelection, setColorSelection ] = useState< string >();
	const [ styleSelection, setStyleSelection ] = useState< string >();

	const isStyleSettable = shouldSanitizeBorder
		? isValidBorder( border )
		: true;

	const onBorderChange = useCallback(
		( newBorder?: Border ) => {
			if ( shouldSanitizeBorder && ! isValidBorder( newBorder ) ) {
				onChange( undefined );
				return;
			}
			onChange( newBorder );
		},
		[ onChange, shouldSanitizeBorder ]
	);

	const onWidthChange = useCallback(
		( newWidth?: string ) => {
			const newWidthValue = newWidth === '' ? undefined : newWidth;
			const [ parsedValue ] =
				parseQuantityAndUnitFromRawValue( newWidth );
			const hasZeroWidth = parsedValue === 0;
			const updatedBorder = { ...border, width: newWidthValue };

			// Setting the border width explicitly to zero will also set the
			// border style to `none` and clear the border color.
			if ( hasZeroWidth && ! hadPreviousZeroWidth ) {
				// Before clearing the color and style selections, keep track of
				// the current selections so they can be restored when the width
				// changes to a non-zero value.
				setColorSelection( border?.color );
				setStyleSelection( border?.style );

				// Clear the color and style border properties.
				updatedBorder.color = undefined;
				updatedBorder.style = 'none';
			}

			// Selection has changed from zero border width to non-zero width.
			if ( ! hasZeroWidth && hadPreviousZeroWidth ) {
				// Restore previous border color and style selections if width
				// is now not zero.
				if ( updatedBorder.color === undefined ) {
					updatedBorder.color = colorSelection;
				}
				if ( updatedBorder.style === 'none' ) {
					updatedBorder.style = styleSelection;
				}
			}

			onBorderChange( updatedBorder );
		},
		[
			border,
			hadPreviousZeroWidth,
			colorSelection,
			styleSelection,
			onBorderChange,
		]
	);

	const onSliderChange = useCallback(
		( value?: number ) => {
			onWidthChange( `${ value }${ widthUnit }` );
		},
		[ onWidthChange, widthUnit ]
	);

	const classes = clsx( styles[ 'border-control' ], className );

	let wrapperWidth = width;
	if ( isCompact ) {
		// Widths below represent the minimum usable width for compact controls.
		// Taller controls contain greater internal padding, thus greater width.
		wrapperWidth = '116px';
	}
	const innerWrapperClassName = clsx(
		styles[ 'inner-wrapper' ],
		!! wrapperWidth && styles[ 'inner-wrapper-has-width' ]
	);

	const sliderClassName = styles.slider;

	return {
		...otherProps,
		className: classes,
		colors,
		enableAlpha,
		enableStyle,
		innerWrapperClassName,
		inputWidth: wrapperWidth,
		isStyleSettable,
		onBorderChange,
		onSliderChange,
		onWidthChange,
		previousStyleSelection: styleSelection,
		sliderClassName,
		value: border,
		widthUnit,
		widthValue,
		__experimentalIsRenderedInSidebar,
	};
}
